import { useEffect } from 'react';
import useGameStore from '../../store/gameStore';
import { MIN_COMPANY_AGE_FOR_LOAN } from '../../data/bankData';
import { marketScaleFromState } from '../../utils/marketScale';

/**
 * Headless component (no render) that listens to game state changes and
 * fires the contextual tutorial dispatcher when a "first time" condition
 * is met. Each condition is checked via a dedicated `useEffect` so we
 * only react to its specific dependency.
 *
 * Triggers wired here :
 *   - `firstEvent`         : when `pendingEvents` becomes non-empty
 *   - `firstTaxDue`        : when `tvaPendingDeclaration` flips to true
 *   - `firstCreditOffer`   : when player has at least 1 vehicle AND has
 *                            reached MIN_COMPANY_AGE_FOR_LOAN days played
 *                            (= 90 days = 3 months). Aligned avec le seuil
 *                            réel des banques pour éviter le message
 *                            trompeur signalé par le user 25/06/2026.
 *   - `firstPhaseUp`       : when `pendingPhaseUp` becomes non-null
 *   - `firstUnhappyDriver` : when ANY driver crosses below 45 % satisfaction
 *   - `firstMA`            : when `pendingAcquisition` becomes non-null (player
 *                            has actually requested a target → first contact
 *                            with the M&A flow)
 *   - `firstLatePayment`   : fired directly from `crmSlice.applyClientEvent`
 *                            (more reliable than a derived state watcher)
 *
 * The store action `triggerContextualTutorial(id)` is idempotent — already
 * seen ids are silently ignored. Safe to fire multiple times.
 */
export default function ContextualTutoTriggers() {
  const pendingEventsCount = useGameStore(s => (s.pendingEvents || []).length);
  const tvaPendingDeclaration = useGameStore(s => s.tvaPendingDeclaration);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const vehiclesCount = useGameStore(s => (s.vehicles || []).length);
  const pendingPhaseUp = useGameStore(s => s.pendingPhaseUp);
  const lowestSatisfaction = useGameStore(s => {
    const drivers = s.drivers || [];
    if (drivers.length === 0) return 100;
    return Math.min(...drivers.map(d => d.satisfaction ?? 100));
  });
  const pendingAcquisition = useGameStore(s => s.pendingAcquisition);
  const tutorialComplete = useGameStore(s => s.tutorialComplete);
  const trigger = useGameStore(s => s.triggerContextualTutorial);
  // 18/06/2026 — évolutions récentes : péages, crédit de TVA, scaling marché.
  const hasTollContract = useGameStore(s => (s.activeContracts || []).some(c => (c.distanceKm || 0) >= 30));
  const tvaCredit = useGameStore(s => Math.max(0, (s.tvaDeductibleTotal || 0) - (s.tvaCollectedTotal || 0)));
  const marketScaleHigh = useGameStore(s => marketScaleFromState(s).competitorScale >= 1.10);

  // The onboarding tour and the contextual tutos compete for screen
  // attention — if the onboarding is still running, defer ALL contextual
  // triggers until it's done.
  const onboardingActive = !tutorialComplete;

  // 1. First event
  useEffect(() => {
    if (!onboardingActive && pendingEventsCount > 0) trigger('firstEvent');
  }, [onboardingActive, pendingEventsCount, trigger]);

  // 2. First TVA declaration due
  useEffect(() => {
    if (!onboardingActive && tvaPendingDeclaration) trigger('firstTaxDue');
  }, [onboardingActive, tvaPendingDeclaration, trigger]);

  // 3. First credit offer — gate on "has at least 1 vehicle AND reached
  // MIN_COMPANY_AGE_FOR_LOAN (90 days = 3 months)". Avant : 30 jours, ce
  // qui prétendait à tort qu'un crédit était possible alors que les
  // banques refusaient encore. Hotfix user 25/06/2026.
  useEffect(() => {
    if (!onboardingActive && vehiclesCount >= 1 && totalDays >= MIN_COMPANY_AGE_FOR_LOAN) {
      trigger('firstCreditOffer');
    }
  }, [onboardingActive, vehiclesCount, totalDays, trigger]);

  // 4. First phase up
  useEffect(() => {
    if (!onboardingActive && pendingPhaseUp) trigger('firstPhaseUp');
  }, [onboardingActive, pendingPhaseUp, trigger]);

  // 5. First unhappy driver
  useEffect(() => {
    if (!onboardingActive && lowestSatisfaction < 45) trigger('firstUnhappyDriver');
  }, [onboardingActive, lowestSatisfaction, trigger]);

  // 6. First M&A — player has actively engaged a target
  useEffect(() => {
    if (!onboardingActive && pendingAcquisition) trigger('firstMA');
  }, [onboardingActive, pendingAcquisition, trigger]);

  // 7. First toll-bearing contract (distance ≥ 30 km → péages)
  useEffect(() => {
    if (!onboardingActive && hasTollContract) trigger('firstTollContract');
  }, [onboardingActive, hasTollContract, trigger]);

  // 8. First TVA credit (CAPEX purchases > TVA collected)
  useEffect(() => {
    if (!onboardingActive && tvaCredit >= 1000) trigger('firstTvaCredit');
  }, [onboardingActive, tvaCredit, trigger]);

  // 9. First market scale-up (competitors + AO growing with the player)
  useEffect(() => {
    if (!onboardingActive && marketScaleHigh) trigger('firstMarketScaleUp');
  }, [onboardingActive, marketScaleHigh, trigger]);

  return null;
}
